import { BootFlags, getBootFlag } from "../app-config/index.js";
import { chronosphereLogsClient } from "../logs/index.js";
import { randomString } from "../utils/index.js";
const ENABLE_FOR_DEV = false;
const debug = (message, ...optionalParams)=>{
    if (ENABLE_FOR_DEV) console.debug(message, ...optionalParams);
};
const submitEventToChronosphereLogs = (message, level, event)=>{
    const eventPayload = {
        source: 'chronosphere-rum',
        message: `[RUM] ${message}`,
        level,
        happened_at: new Date().toISOString(),
        ...event
    };
    debug('Sending to logs...', eventPayload);
    chronosphereLogsClient.submitLog(eventPayload);
};
const getDeviceType = ()=>{
    const ua = window.navigator.userAgent;
    if (/iPad|Macintosh/.test(ua) && 'ontouchend' in document) return 'tablet';
    if (/Android/.test(ua) && !/Mobile/.test(ua)) return 'tablet';
    if (/Mobi|Android/i.test(ua)) return 'mobile';
    return 'desktop';
};
const getBrowserInfo = ()=>{
    const ua = window.navigator.userAgent;
    const uaLower = ua.toLowerCase();
    if (uaLower.includes('ui-prober')) return {
        name: 'Prober',
        version: 'unknown'
    };
    if (uaLower.includes('edg/')) {
        const match = ua.match(/Edg\/([\d.]+)/);
        return {
            name: 'Edge',
            version: match?.[1] ?? 'unknown'
        };
    }
    if (uaLower.includes('opr/') || uaLower.includes('opera')) {
        const match = ua.match(/(?:OPR|Opera)\/([\d.]+)/);
        return {
            name: 'Opera',
            version: match?.[1] ?? 'unknown'
        };
    }
    if (uaLower.includes('chrome')) {
        const match = ua.match(/Chrome\/([\d.]+)/);
        return {
            name: 'Chrome',
            version: match?.[1] ?? 'unknown'
        };
    }
    if (uaLower.includes('safari') && !uaLower.includes('chrome')) {
        const match = ua.match(/Version\/([\d.]+)/);
        return {
            name: 'Safari',
            version: match?.[1] ?? 'unknown'
        };
    }
    if (uaLower.includes('firefox')) {
        const match = ua.match(/Firefox\/([\d.]+)/);
        return {
            name: 'Firefox',
            version: match?.[1] ?? 'unknown'
        };
    }
    if (uaLower.includes('trident') || uaLower.includes('msie')) {
        const match = ua.match(/(?:MSIE |rv:)([\d.]+)/);
        return {
            name: 'IE',
            version: match?.[1] ?? 'unknown'
        };
    }
    return {
        name: 'unknown',
        version: 'unknown'
    };
};
const getOSInfo = ()=>{
    const ua = window.navigator.userAgent;
    if (/Windows NT/.test(ua)) {
        const versionMap = {
            '10.0': '10/11',
            '6.3': '8.1',
            '6.2': '8',
            '6.1': '7'
        };
        const match = ua.match(/Windows NT ([\d.]+)/);
        const ntVersion = match?.[1];
        const version = ntVersion ? versionMap[ntVersion] ?? ntVersion : 'unknown';
        return {
            name: 'Windows',
            version
        };
    }
    if (/Mac OS X/.test(ua)) {
        const match = ua.match(/Mac OS X ([\d_]+)/);
        let version = match?.[1]?.replace(/_/g, '.') ?? 'unknown';
        if ('10.15' === version) version = '10.15+';
        return {
            name: 'macOS',
            version
        };
    }
    if (/iPhone|iPad|iPod/.test(ua)) {
        const match = ua.match(/OS ([\d_]+)/);
        const version = match?.[1]?.replace(/_/g, '.') ?? 'unknown';
        return {
            name: 'iOS',
            version
        };
    }
    if (/Android/.test(ua)) {
        const match = ua.match(/Android ([\d.]+)/);
        const version = match?.[1] ?? 'unknown';
        return {
            name: 'Android',
            version
        };
    }
    if (/Linux/.test(ua)) return {
        name: 'Linux',
        version: 'unknown'
    };
    if (/CrOS/.test(ua)) return {
        name: 'Chrome OS',
        version: 'unknown'
    };
    return {
        name: 'unknown',
        version: 'unknown'
    };
};
const getDeviceModel = ()=>{
    const ua = window.navigator.userAgent;
    if (/iPhone/.test(ua)) {
        const { width, height } = window.screen;
        const deviceMap = {
            '390x844': 'iPhone 12/13/14',
            '393x852': 'iPhone 14 Pro',
            '428x926': 'iPhone 12/13/14 Pro Max',
            '430x932': 'iPhone 14 Pro Max',
            '375x812': 'iPhone X/XS/11 Pro',
            '414x896': 'iPhone XR/11/11 Pro Max',
            '375x667': 'iPhone 6/7/8',
            '414x736': 'iPhone 6/7/8 Plus',
            '320x568': 'iPhone SE (1st gen)'
        };
        return deviceMap[`${width}x${height}`] ?? 'iPhone';
    }
    if (/iPad/.test(ua) || /Macintosh/.test(ua) && 'ontouchend' in document) return 'iPad';
    if (/Android/.test(ua)) {
        const match = ua.match(/Android.*;\s([^)]+)\sBuild/);
        const model = match?.[1];
        if (model) return model.replace(/^(SM-|SAMSUNG-|LG-|HTC-)/, '');
        return 'Android Device';
    }
};
const SCREEN_RESOLUTION_STANDARDS = [
    {
        w: 1920,
        h: 1080,
        name: 'Full HD'
    },
    {
        w: 2560,
        h: 1440,
        name: '2K'
    },
    {
        w: 3840,
        h: 2160,
        name: '4K'
    },
    {
        w: 1366,
        h: 768,
        name: 'HD'
    },
    {
        w: 1440,
        h: 900,
        name: 'MacBook'
    },
    {
        w: 2880,
        h: 1800,
        name: 'MacBook Pro Retina'
    },
    {
        w: 1536,
        h: 864,
        name: 'HD+'
    },
    {
        w: 1280,
        h: 720,
        name: 'HD'
    },
    {
        w: 1600,
        h: 900,
        name: 'HD+'
    },
    {
        w: 2560,
        h: 1600,
        name: 'MacBook Pro 13"'
    },
    {
        w: 3024,
        h: 1964,
        name: 'MacBook Pro 14"'
    },
    {
        w: 3456,
        h: 2234,
        name: 'MacBook Pro 16"'
    }
];
const normalizeResolution = (width, height)=>{
    for (const std of SCREEN_RESOLUTION_STANDARDS)if (Math.abs(width - std.w) <= 10 && Math.abs(height - std.h) <= 10) return `${std.w}x${std.h}`;
    const roundedW = 10 * Math.round(width / 10);
    const roundedH = 10 * Math.round(height / 10);
    return `${roundedW}x${roundedH}`;
};
const VIEWPORT_BREAKPOINTS = [
    {
        name: 'xs',
        minWidth: 0,
        maxWidth: 639,
        label: 'Mobile'
    },
    {
        name: 'sm',
        minWidth: 640,
        maxWidth: 767,
        label: 'Large Mobile'
    },
    {
        name: 'md',
        minWidth: 768,
        maxWidth: 1023,
        label: 'Tablet'
    },
    {
        name: 'lg',
        minWidth: 1024,
        maxWidth: 1279,
        label: 'Small Desktop'
    },
    {
        name: 'xl',
        minWidth: 1280,
        maxWidth: 1535,
        label: 'Desktop'
    },
    {
        name: '2xl',
        minWidth: 1536,
        maxWidth: 1 / 0,
        label: 'Large Desktop'
    }
];
const getViewportBreakpoint = (width, height)=>{
    const breakpoint = (()=>{
        if (width < 640) return 'xs';
        if (width < 768) return 'sm';
        if (width < 1024) return 'md';
        if (width < 1280) return 'lg';
        if (width < 1536) return 'xl';
        return '2xl';
    })();
    const roundedW = 10 * Math.round(width / 10);
    const roundedH = 10 * Math.round(height / 10);
    return `${breakpoint} (${roundedW}x${roundedH})`;
};
let sessionId;
const getSessionId = ()=>{
    if (!sessionId) sessionId = randomString();
    return sessionId;
};
const getCommonMetadata = ()=>{
    const browserInfo = getBrowserInfo();
    const osInfo = getOSInfo();
    const deviceModel = getDeviceModel();
    return {
        'rum.sessionId': getSessionId(),
        'rum.url': window.location.href,
        'rum.pathName': window.location.pathname,
        'rum.hostName': window.location.hostname,
        'rum.pageName': window.document.title.replace('- Chronosphere', ''),
        'rum.browserName': browserInfo.name,
        'rum.browserVersion': browserInfo.version,
        'rum.os': osInfo.name,
        'rum.osVersion': osInfo.version,
        'rum.deviceType': getDeviceType(),
        ...deviceModel && {
            'rum.deviceModel': deviceModel
        },
        'rum.screenResolution': normalizeResolution(window.screen.width, window.screen.height),
        'rum.viewport': getViewportBreakpoint(window.innerWidth, window.innerHeight),
        'rum.timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
        'rum.language': window.navigator.language
    };
};
const observeNavigationTiming = ()=>{
    window.addEventListener('load', ()=>{
        setTimeout(()=>{
            const perfData = performance.getEntriesByType('navigation')[0];
            if (perfData) submitEventToChronosphereLogs('Navigation timing captured', 'info', {
                ...getCommonMetadata(),
                'rum.metricType': 'navigation',
                'rum.dnsLookup': perfData.domainLookupEnd - perfData.domainLookupStart,
                'rum.tcpConnection': perfData.connectEnd - perfData.connectStart,
                'rum.tlsNegotiation': perfData.secureConnectionStart > 0 ? perfData.connectEnd - perfData.secureConnectionStart : 0,
                'rum.ttfb': perfData.responseStart - perfData.requestStart,
                'rum.responseTime': perfData.responseEnd - perfData.responseStart,
                'rum.domInteractive': perfData.domInteractive,
                'rum.domContentLoaded': perfData.domContentLoadedEventEnd - perfData.domContentLoadedEventStart,
                'rum.domComplete': perfData.domComplete,
                'rum.loadComplete': perfData.loadEventEnd - perfData.loadEventStart,
                'rum.totalLoadTime': perfData.loadEventEnd - perfData.fetchStart
            });
        }, 0);
    });
};
const observeResourceTiming = ()=>{
    const captureResources = ()=>{
        const resources = performance.getEntriesByType('resource');
        const recentResources = resources.slice(-50);
        const networkRequests = [];
        const staticAssets = new Map();
        recentResources.forEach((resource)=>{
            const type = resource.initiatorType;
            if ('fetch' === type || 'xmlhttprequest' === type) networkRequests.push(resource);
            else {
                if (!staticAssets.has(type)) staticAssets.set(type, []);
                staticAssets.get(type).push(resource);
            }
        });
        networkRequests.forEach((resource)=>{
            let requestPath = resource.name;
            try {
                const urlObj = new URL(resource.name);
                requestPath = urlObj.pathname + urlObj.search;
            } catch  {
                requestPath = resource.name;
            }
            const responseStatus = resource.responseStatus;
            submitEventToChronosphereLogs('Network request', 'info', {
                ...getCommonMetadata(),
                'rum.metricType': 'networkRequest',
                'rum.requestUrl': resource.name,
                'rum.requestPath': requestPath,
                'rum.requestType': resource.initiatorType,
                'rum.duration': resource.duration,
                'rum.transferSize': resource.transferSize,
                'rum.decodedBodySize': resource.decodedBodySize,
                'rum.startTime': resource.startTime,
                'rum.cached': 0 === resource.transferSize && resource.decodedBodySize > 0,
                ...void 0 !== responseStatus && {
                    'rum.statusCode': responseStatus
                },
                'rum.dnsTime': resource.domainLookupEnd - resource.domainLookupStart,
                'rum.tcpTime': resource.connectEnd - resource.connectStart,
                'rum.requestTime': resource.responseStart - resource.requestStart,
                'rum.responseTime': resource.responseEnd - resource.responseStart
            });
        });
        staticAssets.forEach((assetList, type)=>{
            const items = assetList.map((resource)=>({
                    name: resource.name,
                    duration: resource.duration,
                    transferSize: resource.transferSize,
                    decodedBodySize: resource.decodedBodySize,
                    startTime: resource.startTime,
                    cached: 0 === resource.transferSize && resource.decodedBodySize > 0
                }));
            const totalSize = items.reduce((sum, item)=>sum + item.transferSize, 0);
            const avgDuration = items.reduce((sum, item)=>sum + item.duration, 0) / items.length;
            const maxDuration = Math.max(...items.map((item)=>item.duration));
            const cachedCount = items.filter((item)=>item.cached).length;
            submitEventToChronosphereLogs(`Static assets loaded: ${type}`, 'info', {
                ...getCommonMetadata(),
                'rum.metricType': 'staticAssets',
                'rum.assetType': type,
                'rum.count': items.length,
                'rum.totalSize': totalSize,
                'rum.avgDuration': avgDuration,
                'rum.maxDuration': maxDuration,
                'rum.cachedCount': cachedCount,
                'rum.cacheRate': cachedCount / items.length,
                'rum.items': items
            });
        });
        performance.clearResourceTimings();
    };
    setInterval(captureResources, 30000);
};
const observeWebVitals = ()=>{
    if (!('PerformanceObserver' in window)) return void debug('PerformanceObserver not supported, skipping web vitals');
    try {
        let lcpValue;
        let lcpElement;
        let lcpReported = false;
        const lcpObserver = new PerformanceObserver((list)=>{
            const entries = list.getEntries();
            const lastEntry = entries[entries.length - 1];
            lcpValue = lastEntry.renderTime || lastEntry.loadTime;
            lcpElement = lastEntry.element?.tagName;
        });
        lcpObserver.observe({
            type: 'largest-contentful-paint',
            buffered: true
        });
        const reportLCP = ()=>{
            if (lcpReported || void 0 === lcpValue) return;
            lcpReported = true;
            submitEventToChronosphereLogs('LCP metric captured', 'info', {
                ...getCommonMetadata(),
                'rum.metricType': 'lcp',
                'rum.value': lcpValue,
                'rum.element': lcpElement
            });
        };
        document.addEventListener('visibilitychange', ()=>{
            if ('hidden' === document.visibilityState) reportLCP();
        });
        window.addEventListener('pagehide', reportLCP);
        const fidObserver = new PerformanceObserver((list)=>{
            const entries = list.getEntries();
            entries.forEach((entry)=>{
                const fidEntry = entry;
                submitEventToChronosphereLogs('FID metric captured', 'info', {
                    ...getCommonMetadata(),
                    'rum.metricType': 'fid',
                    'rum.value': fidEntry.processingStart - fidEntry.startTime,
                    'rum.eventType': entry.name
                });
            });
        });
        fidObserver.observe({
            type: 'first-input',
            buffered: true
        });
        let clsScore = 0;
        let clsReported = false;
        const clsObserver = new PerformanceObserver((list)=>{
            for (const entry of list.getEntries()){
                const layoutShift = entry;
                if (!layoutShift.hadRecentInput) clsScore += layoutShift.value;
            }
        });
        clsObserver.observe({
            type: 'layout-shift',
            buffered: true
        });
        const reportCLS = ()=>{
            if (clsReported) return;
            clsReported = true;
            submitEventToChronosphereLogs('CLS metric captured', 'info', {
                ...getCommonMetadata(),
                'rum.metricType': 'cls',
                'rum.value': clsScore
            });
        };
        document.addEventListener('visibilitychange', ()=>{
            if ('hidden' === document.visibilityState) reportCLS();
        });
        window.addEventListener('pagehide', reportCLS);
    } catch (e) {
        console.warn('Web Vitals observation failed:', e);
    }
};
const observeErrors = ()=>{
    window.addEventListener('error', (event)=>{
        submitEventToChronosphereLogs('JavaScript error', 'error', {
            ...getCommonMetadata(),
            'rum.metricType': 'error',
            'rum.message': event.message,
            'rum.filename': event.filename,
            'rum.lineno': event.lineno,
            'rum.colno': event.colno,
            'rum.stack': event.error?.stack
        });
    });
    window.addEventListener('unhandledrejection', (event)=>{
        submitEventToChronosphereLogs('Unhandled promise rejection', 'error', {
            ...getCommonMetadata(),
            'rum.metricType': 'unhandledRejection',
            'rum.reason': event.reason?.message || String(event.reason),
            'rum.stack': event.reason?.stack
        });
    });
};
const observeLongTasks = ()=>{
    if (!('PerformanceObserver' in window)) return void debug('PerformanceObserver not supported, skipping long tasks');
    try {
        const longTaskObserver = new PerformanceObserver((list)=>{
            for (const entry of list.getEntries())submitEventToChronosphereLogs('Long task detected', 'info', {
                ...getCommonMetadata(),
                'rum.metricType': 'longTask',
                'rum.duration': entry.duration,
                'rum.startTime': entry.startTime
            });
        });
        longTaskObserver.observe({
            type: 'longtask',
            buffered: true
        });
    } catch (e) {
        debug('Long task observation not supported:', e);
    }
};
const initializeRum = ()=>{
    try {
        const isEnabled = ENABLE_FOR_DEV || 'true' === getBootFlag(BootFlags.NETWORK_MONITORING_ENABLED);
        if (!isEnabled) return;
        debug('Initializing rum...');
        observeNavigationTiming();
        observeResourceTiming();
        observeWebVitals();
        observeErrors();
        observeLongTasks();
    } catch  {
        debug('Unable to initialize RUM!');
    }
};
export { SCREEN_RESOLUTION_STANDARDS, VIEWPORT_BREAKPOINTS, initializeRum };

//# sourceMappingURL=initialize-rum.js.map